import axios, { AxiosError } from "axios";

import { API_ERROR_MESSAGE } from "@/constants";
import type { AppApiError } from "@/types";
import { readString, toRecord } from "@/utils/normalize";

const readApiPayload = (data: unknown) => {
  const record = toRecord(data);
  if (!record) {
    return null;
  }

  const nestedError = toRecord(record.error);
  return nestedError ?? record;
};

export const createAppApiError = ({
  action = null,
  code = null,
  details,
  message,
  status = null,
}: {
  action?: string | null;
  code?: string | null;
  details?: unknown;
  message?: string | null;
  status?: number | null;
}): AppApiError =>
  Object.assign(new Error(message || API_ERROR_MESSAGE.DEFAULT), {
    name: "AppApiError" as const,
    status,
    code,
    action,
    details,
    isAppApiError: true as const,
  });

export const isAppApiError = (error: unknown): error is AppApiError =>
  typeof error === "object" &&
  error !== null &&
  "isAppApiError" in error &&
  (error as { isAppApiError?: boolean }).isAppApiError === true;

const normalizeAxiosError = (
  error: AxiosError,
  fallbackMessage?: string,
): AppApiError => {
  const payload = readApiPayload(error.response?.data);
  const status = error.response?.status ?? null;

  return createAppApiError({
    action: readString(payload?.action) ?? null,
    code: readString(payload?.code) ?? null,
    details: error.response?.data,
    message:
      readString(payload?.message) ??
      readString(error.response?.statusText) ??
      readString(error.message) ??
      fallbackMessage ??
      API_ERROR_MESSAGE.DEFAULT,
    status,
  });
};

export const normalizeApiError = (
  error: unknown,
  fallbackMessage?: string,
): AppApiError => {
  if (isAppApiError(error)) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    return normalizeAxiosError(error, fallbackMessage);
  }

  if (error instanceof Error) {
    return createAppApiError({
      details: error,
      message: error.message || fallbackMessage || API_ERROR_MESSAGE.DEFAULT,
    });
  }

  return createAppApiError({
    details: error,
    message: fallbackMessage || API_ERROR_MESSAGE.DEFAULT,
  });
};
